import { CalculatorIcon, BanknotesIcon, EnvelopeIcon, PowerIcon, TicketIcon, UserCircleIcon, UsersIcon, TagIcon } from "@heroicons/react/24/outline";
import { ReactComponent as Logo } from "assets/images/logo.svg";
import Button, { EButtonVariant } from "components/elements/Button";
import Typography, { ITypo, ITypoColor } from "components/elements/Typography";
import ModuleConfig from "configs/ModuleConfig";
import { UserContext } from "contexts/User";
import { useContext } from "react";
import { Link } from "react-router-dom";
import { container } from "tsyringe";

import I18n from "../I18n";
import classes from "./classes.module.scss";
import MenuLink from "./MenuLink";
import HasInvestorPortalAccess from "../HasInvestorPortalAccess";
import HasRules from "../HasRules";
import ModulePage from "../ModulePage";

const pages = container.resolve(ModuleConfig).get().modules.pages;

export default function NavMenu() {
	const { user, logout } = useContext(UserContext);
	function getAllNestedPaths(page: any): string[] {
		let paths: string[] = [];
		if (page.props.path) {
			paths.push(page.props.path);
		}
		if (page.props.pages) {
			for (const nestedPage of Object.values(page.props.pages)) {
				paths = paths.concat(getAllNestedPaths(nestedPage));
			}
		}
		return paths;
	}

	return (
		<div className={classes["root"]}>
			<Link to={pages.Products.props.path}>
				<Logo className={classes["logo"]} />
			</Link>
			<div className={classes["container"]}>
				<nav>
					<HasRules requiredRules={{ OR: { access_platform__cgp: true, access_platform__middle_office: true } }}>
						<ModulePage from={pages.Clients}>
							<MenuLink
								title={I18n.asset.pages.products.page_title}
								icon={<TagIcon />}
								to={pages.Products.props.path}
								matchingPaths={getAllNestedPaths(pages.Products)}
							/>
						</ModulePage>
					</HasRules>

					<HasRules requiredRules={{ AND: { access_platform__cgp: true } }}>
						<ModulePage from={pages.Subscriptions}>
							<MenuLink
								title={I18n.asset.pages.subscriptions.page_title}
								icon={<TicketIcon />}
								to={pages.Subscriptions.props.pages.InProgress.props.path}
								matchingPaths={getAllNestedPaths(pages.Subscriptions)}
							/>
						</ModulePage>
					</HasRules>

					<HasRules requiredRules={{ AND: { access_platform__cgp: true } }}>
						<ModulePage from={pages.Clients}>
							<MenuLink
								title={I18n.asset.pages.clients.page_title}
								icon={<UsersIcon />}
								to={pages.Clients.props.pages.PhysicalPersonClients.props.path}
								matchingPaths={getAllNestedPaths(pages.Clients)}
							/>
						</ModulePage>
					</HasRules>

					<HasRules requiredRules={{ OR: { access_platform__cgp: true, access_platform__middle_office: true } }}>
						<ModulePage from={pages.InvestorInvitations}>
							<HasInvestorPortalAccess>
								<MenuLink
									title={I18n.asset.pages.investor_invitations.page_title}
									icon={<EnvelopeIcon />}
									to={pages.InvestorInvitations.props.path}
									matchingPaths={getAllNestedPaths(pages.InvestorInvitations)}
								/>
							</HasInvestorPortalAccess>
						</ModulePage>
					</HasRules>

					<HasRules requiredRules={{ AND: { access_platform__cgp: true } }}>
						<ModulePage from={pages.Consortium}>
							<MenuLink
								title={I18n.asset.pages.consortium.page_title}
								icon={<BanknotesIcon />}
								to={pages.Consortium.props.path}
								matchingPaths={getAllNestedPaths(pages.Consortium)}
							/>
						</ModulePage>
					</HasRules>

					<HasRules requiredRules={{ AND: { access_platform__middle_office: true } }}>
						<ModulePage from={pages.MOSubscriptions}>
							<MenuLink
								title={I18n.asset.pages.mo_subscriptions.page_title}
								icon={<TicketIcon />}
								to={pages.MOSubscriptions.props.pages.ToProcess.props.path}
								matchingPaths={getAllNestedPaths(pages.MOSubscriptions)}
							/>
						</ModulePage>
					</HasRules>

					<HasRules requiredRules={{ AND: { access_platform__middle_office: true } }}>
						<ModulePage from={pages.Partners}>
							<MenuLink
								title={I18n.asset.pages.partners.page_title}
								icon={<UsersIcon />}
								to={pages.Partners.props.pages.OfficesList.props.path}
								matchingPaths={getAllNestedPaths(pages.Partners)}
							/>
						</ModulePage>
					</HasRules>

					<HasRules requiredRules={{ OR: { access_platform__cgp: true, access_platform__middle_office: true } }}>
						<ModulePage from={pages.Simulator}>
							<MenuLink title="Simulateur" icon={<CalculatorIcon />} to={pages.Simulator.props.path} matchingPaths={[pages.Simulator.props.path]} />
						</ModulePage>
					</HasRules>

					<HasRules
						requiredRules={{
							OR: {
								access_platform__cgp: true,
								access_platform__middle_office: true,
							},
						}}>
						<ModulePage from={pages.Account}>
							<MenuLink
								title={I18n.asset.pages.account.page_title}
								icon={<UserCircleIcon />}
								to={pages.Account.props.path}
								matchingPaths={getAllNestedPaths(pages.Account)}
							/>
						</ModulePage>
					</HasRules>
				</nav>
				<div className={classes["bottom"]}>
					<div className={classes["contact"]}>
						<Typography typo={ITypo.P_SMALL} color={ITypoColor.BLACK}>
							{I18n.asset.component.menu.question}
						</Typography>
						<a href="mailto:backoffice@wenimmo.com">
							<Typography style={{ textDecoration: "underline" }} typo={ITypo.P_SMALL} color={ITypoColor.BLACK}>
								{I18n.asset.component.menu.contact}
							</Typography>
						</a>
					</div>
					{user && (
						<Button variant={EButtonVariant.OUTLINED} icon={<PowerIcon />} iconposition="left" onClick={logout} fullwidth>
							{I18n.asset.common.logout}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
